/* Reconciliation — System Assumptions Panel (STA-6 · Feb 2026)
   ==================================================================
   Collapsible card that surfaces every runtime assumption the
   Reconciliation Engine relied on. Turns opaque numbers into an
   auditable story: "we used invoice_date, we counted posted+approved
   overhead, your Cash Registry has 3 accounts, etc."
   Rendered at the top of the Reconciliation Center — so any reader
   (CFO, auditor, external accountant) can immediately understand
   the INPUTS before questioning the OUTPUTS. */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Settings2 } from 'lucide-react';

const ROWS = [
  { key: 'revenue_source',
    label_ar: 'مصدر الإيراد',
    hint_ar: 'يستخدم النظام تاريخ الفاتورة الطبيعي (invoice_date) لتحديد فترة الإيراد — وليس تاريخ الإدخال في النظام (created_at) — لأنه يطابق تاريخ القيد المحاسبي.' },
  { key: 'ar_valuation_basis',
    label_ar: 'أساس تقييم الذمم المدينة',
    hint_ar: 'رصيد لحظي مفلتَر بحيث لا تُحتسب الفواتير التي تاريخها بعد نهاية الفترة (لأن قيودها لم تسجَّل بعد).' },
  { key: 'vat_source',
    label_ar: 'مصدر بيانات ضريبة القيمة المضافة',
    hint_ar: 'يجمع النظام VAT من القيود المُرحَّلة والإقرار الضريبي المُقدَّم (snapshot). يدعم فواتير الموردين، النفقات، والتسويات البنكية.' },
  { key: 'cash_registry_configured',
    label_ar: 'سجل الحسابات النقدية',
    hint_ar: 'إذا كان السجل فارغاً، يتحوّل فحص النقدية إلى NOT_CONFIGURED بدلاً من FAIL كاذب.',
    isBoolean: true },
  { key: 'cash_accounts_registered',
    label_ar: 'عدد الحسابات النقدية المُسجَّلة',
    hint_ar: 'حسابات البنك والنقدية التي يعتبرها النظام مصدراً لحركة السيولة.' },
  { key: 'project_allocation_enabled',
    label_ar: 'تفعيل تخصيص المشاريع',
    hint_ar: 'إذا لم توجد مشاريع، يقلّل النظام Confidence لفحص Project Profit.',
    isBoolean: true },
  { key: 'projects_count',
    label_ar: 'عدد المشاريع',
    hint_ar: 'إجمالي المشاريع المُسجَّلة للشركة.' },
  { key: 'overhead_statuses_counted',
    label_ar: 'حالات سيناريوهات المصاريف العامة المشمولة',
    hint_ar: 'يُحتسب كلاً من posted (المُرحَّل) و approved (المُعتمَد لكن لم يُرحَّل بعد) — استبعاد approved كان يُنتج تحذيرات كاذبة.',
    isArray: true },
  { key: 'overhead_statuses_excluded',
    label_ar: 'الحالات المستثناة',
    hint_ar: 'reversed و draft لا تُدخل في الحساب.',
    isArray: true },
  { key: 'tolerance_sar',
    label_ar: 'السماحية (SAR)',
    hint_ar: 'أي فرق ≤ هذه القيمة يُعتبر تقريباً حسابياً وليس خطأً حقيقياً.',
    suffix: 'ريال' },
  { key: 'warning_threshold_sar',
    label_ar: 'حدّ التحذير (SAR)',
    hint_ar: 'أي فرق > هذا الحد يرفع الحالة إلى FAIL بدلاً من WARNING.',
    suffix: 'ريال' },
  { key: 'not_due_yet_semantics',
    label_ar: 'دلالة NOT_DUE_YET',
    hint_ar: 'حالات فحص لم يحن موعد استحقاقها بعد (مثلاً إقرار ربعي أثناء الشهر الأول).' },
  { key: 'not_configured_semantics',
    label_ar: 'دلالة NOT_CONFIGURED',
    hint_ar: 'فحوصات تحتاج إعدادات لم يكملها المستأجر بعد — لا تُحسب في Integrity Score.' },
];

function renderVal(row, val) {
  if (val === undefined || val === null) return '—';
  if (row.isBoolean) {
    return val
      ? <span className="text-emerald-700 font-bold">مُهيَّأ ✓</span>
      : <span className="text-rose-700 font-bold">غير مُهيَّأ ✗</span>;
  }
  if (row.isArray && Array.isArray(val)) {
    return (
      <span className="flex flex-wrap gap-1">
        {val.map(v => (
          <span key={v} className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[10px] font-mono">
            {v}
          </span>
        ))}
      </span>
    );
  }
  if (typeof val === 'number') {
    return <span className="font-mono">{val.toLocaleString('en-US')}{row.suffix ? ` ${row.suffix}` : ''}</span>;
  }
  return <span className="font-mono text-[11px]">{String(val)}</span>;
}

export default function SystemAssumptionsPanel({ assumptions }) {
  const [open, setOpen] = useState(false);
  if (!assumptions) return null;
  const registryOK = assumptions.cash_registry_configured;
  const projectsOK = assumptions.project_allocation_enabled;
  const warnings = [];
  if (!registryOK) warnings.push('Cash Registry فارغ');
  if (!projectsOK) warnings.push('لا توجد مشاريع');

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden"
         data-testid="system-assumptions-panel">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
        data-testid="system-assumptions-toggle"
      >
        <div className="flex items-center gap-2">
          <Settings2 size={16} className="text-slate-600" />
          <div>
            <div className="text-sm font-bold text-slate-900">System Assumptions</div>
            <div className="text-[11px] text-slate-500">
              كل الافتراضات التي بُني عليها هذا الفحص — راجعها قبل الحكم على أي رقم.
              {warnings.length > 0 && (
                <span className="ml-2 text-amber-700 font-semibold">
                  · تنبيه: {warnings.join(' · ')}
                </span>
              )}
            </div>
          </div>
        </div>
        {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-3">
          {ROWS.map(row => (
            <div key={row.key} className="bg-slate-50 border border-slate-200 rounded p-2.5"
                 data-testid={`assumption-${row.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-bold text-slate-800">{row.label_ar}</div>
                <div className="text-xs">{renderVal(row, assumptions[row.key])}</div>
              </div>
              <div className="text-[10px] text-slate-600 mt-1 leading-relaxed">{row.hint_ar}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
